import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { REASON_GLYPH, REASON_LABEL, REASON_TONE } from './reasons';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { attentionApi, type RosterRow } from '@/lib/api/attention';
import { cn } from '@/lib/cn';

/**
 * Every student, sortable by any column.
 *
 * The queue answers "who needs me" and is deliberately short. This answers "how
 * is everyone", which a trainer asks too — usually before a review, and usually
 * sorted by a column the queue does not have. Seventy rows is a browsable size;
 * it is only the thousand problems behind them that were not.
 */

type SortKey = 'name' | 'coverage_pct' | 'solved_last_7' | 'to_ask' | 'idle_days';

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: 'name', label: 'Student' },
  { key: 'coverage_pct', label: 'Coverage', numeric: true },
  { key: 'solved_last_7', label: 'Solved this week', numeric: true },
  { key: 'to_ask', label: 'To ask about', numeric: true },
  { key: 'idle_days', label: 'Last active', numeric: true },
];

export function RosterTable({ batchId }: { batchId: string }) {
  // Defaults to the integrity column, descending. Sorting by it is the thing
  // the abandoned heatmap was supposed to make possible — here it is one click,
  // with no hunting for dark squares.
  const [sort, setSort] = useState<SortKey>('to_ask');
  const [desc, setDesc] = useState(true);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attention', 'roster', batchId],
    queryFn: async () => (await attentionApi.getRoster(batchId)).data,
  });

  const rows = useMemo(() => {
    const list = [...(data ?? [])];
    list.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name) * (desc ? -1 : 1);
      return ((a[sort] as number) - (b[sort] as number)) * (desc ? -1 : 1);
    });
    return list;
  }, [data, sort, desc]);

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <span className="sr-only">Loading the roster…</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">The roster could not be loaded.</p>
        <Button variant="link" onClick={() => void refetch()} className="mt-1">
          Try again
        </Button>
      </Card>
    );
  }

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No students enrolled in this batch.</p>;
  }

  const toggle = (key: SortKey) => {
    if (key === sort) {
      setDesc((d) => !d);
    } else {
      setSort(key);
      // Names read naturally A–Z; every other column is more useful worst-first.
      setDesc(key !== 'name');
    }
  };

  return (
    <Card className="overflow-hidden shadow-none">
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((c) => (
              <TableHead
                key={c.key}
                aria-sort={sort === c.key ? (desc ? 'descending' : 'ascending') : 'none'}
                className={cn('whitespace-nowrap', c.numeric && 'text-right')}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggle(c.key)}
                  className={cn('-mx-2 h-8 px-2 text-xs', sort === c.key && 'text-foreground')}
                >
                  {c.label}
                  {sort === c.key && <span aria-hidden="true">{desc ? '↓' : '↑'}</span>}
                </Button>
              </TableHead>
            ))}
            <TableHead>Flag</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <RosterRowView key={r.user_id} row={r} />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function RosterRowView({ row }: { row: RosterRow }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap font-medium">{row.name}</TableCell>

      <TableCell className="whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">
          <Progress value={Math.min(100, Math.max(0, row.coverage_pct))} className="h-1.5 w-14" />
          <span className="tabular-nums text-muted-foreground">
            {Math.round(row.coverage_pct)}%
          </span>
        </div>
      </TableCell>

      <TableCell className="text-right tabular-nums text-muted-foreground">
        {row.solved_last_7}
      </TableCell>

      {/* An em dash, not a zero: nothing to ask about should not read as a score. */}
      <TableCell
        className={cn(
          'text-right tabular-nums',
          row.to_ask > 0 ? 'font-semibold text-sky-700' : 'text-muted-foreground/60',
        )}
      >
        {row.to_ask > 0 ? row.to_ask : '—'}
      </TableCell>

      <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
        {!row.ever_active ? 'never' : row.idle_days === 0 ? 'today' : `${row.idle_days}d ago`}
      </TableCell>

      <TableCell className="whitespace-nowrap">
        {row.reason ? (
          <Badge variant="outline" className={cn('gap-1 py-px', REASON_TONE[row.reason])}>
            <span aria-hidden="true">{REASON_GLYPH[row.reason]}</span>
            {REASON_LABEL[row.reason]}
          </Badge>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}
